/**
 * walk-scene entry point — multi-scene orchestration.
 *
 * Boot fetches a runtime registry (`/assets/scenes/registry.json`) and loads the
 * entry scene via SceneManager. `switchTo(sceneId, spawn, angles)` drives every
 * scene transition: it fades out, unloads the current scene (freeing its gsplat
 * VRAM), loads the new manifest + content, re-binds the UI, re-seats the player
 * (new collision / spawn / mode), and fades back in. Walking into a portal emits
 * PORTAL_ENTER, which calls switchTo with the portal's target scene; the target
 * scene manifest owns the spawn point unless a caller explicitly overrides it.
 *
 * The single update loop drives input → active controller → rig → managers, but
 * is gated while a switch is in progress so the player can't move mid-transition.
 */

import './styles.css';
import { bootstrapBusiness } from './platform/pages';
import { RoomSession } from './network/RoomSession';
import { RemotePlayers, modelConfig } from './network/RemotePlayers';
import { classifyLocomotion } from './network/locomotion';
import type { Admission, PlayerState, ActionState, Avatar } from '../../../vrroom_typescript/src/platform/contracts';

import { Vec3, GSPLAT_DEBUG_NONE, GSPLAT_DEBUG_LOD, GSPLAT_DEBUG_NODE_AABBS } from 'playcanvas';

import { createApp, isMobileRuntime } from './app/createApp';
import { installPortraitKeyboardSheet } from './ui/MobileKeyboardSheet';
import { globalConfig } from './config/global.config';
import { fetchRegistry, fetchSceneManifest } from './config/manifest';
import type { SceneManifest } from './config/manifest';
import { eventBus } from './events/EventBus';
import { InputController } from './input/input-controller';
import { PointerLockManager } from './input/pointer-lock';
import { CameraState } from './player/types';
import { FirstPersonController } from './player/FirstPersonController';
import { CharacterController } from './player/CharacterController';
import { PlayerRig } from './player/PlayerRig';
import { ThirdPersonController } from './player/ThirdPersonController';
import { AvatarController } from './player/AvatarController';
import { AnimationStateMachine } from './player/AnimationStateMachine';
import { avatarConfig } from './config/avatar.config';
import type { CameraController } from './player/types';
import { SceneManager } from './scene/SceneManager';
import { preloadGaussianScene, startFollowPreload, setGaussianChunkConcurrency, BACKGROUND_CHUNK_LOAD_CONCURRENCY } from './scene/loadGaussianScene';
import { SceneEditor } from './scene/SceneEditor';
import { EditorToolbar } from './ui/EditorToolbar';
import { EditorAssetLibrary } from './editor/EditorAssetLibrary';
import { UiManager } from './ui/UiManager';
import type { VoiceCredentials } from './voice/VoiceClient';
import { LazyVoice } from './voice/LazyVoice';
import { VoiceControls } from './ui/VoiceControls';
import { WorldUI } from './ui/WorldUI';
import { HostObjectTools } from './ui/HostObjectTools';
import { HudControls } from './ui/HudControls';
import type { Collision } from './collision';

function isEditableTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    const tag = target.tagName;
    return target.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

type RenderGateApp = { renderNextFrame?: boolean };

const MOBILE_RENDER_IDLE_SECONDS = 4;
const MOBILE_RENDER_POS_EPS_SQ = 0.00001;
const MOBILE_RENDER_ANGLE_EPS_SQ = 0.0001;
const TOUCH_TAP_MOVE_THRESHOLD = 10;
const TOUCH_TAP_MAX_MS = 350;
// Mobile background near-field gsplat sharpening (see switchTo). Deliberately
// bounded: small radius + LOD-capped, unlike the old mobile 'full' background
// preload that swept every coarsest chunk and OOM-killed meihuashan.
const MOBILE_NEAR_PRELOAD_DELAY_MS = 650;  // after collision/occlusion kick off, alongside skybox
const MOBILE_NEAR_PRELOAD_LOD = 1;         // 渲染上限=LOD1（手机不用 LOD0，真机实测脚下整片红且性价比差）；出生点近场预载与上限对齐
const MOBILE_NEAR_PRELOAD_RADIUS_M = 12;
// 朝向跟随分环预载（视线前方最高精度）：LOD1（=渲染上限）→LOD2→LOD3→
// LOD4→LOD5 逐级外推到 48m，此表为「前方扇区」的档位；侧后方（含视线外
// 的脚下）由 startFollowPreload 自动粗一档——最高精度只留给注视方向，
// 脚下省一档额度让给视线前方，引擎侧 foveation 0.8 中心加权同向配合。
// 档位=引擎在该距离该方向想渲染的档位；外环体积小、前方优先、每 tick 限量 8。
const MOBILE_NEAR_PRELOAD_RINGS: Array<[number, number]> = [[6, 1], [12, 2], [20, 3], [32, 4], [48, 5]];

async function main() {
    // 启动阶段指示：若卡在「正在启动…」，loader 文案能反映当前卡点（诊断用）。
    const bootStage = (text: string) => {
        const el = document.getElementById('loader-text');
        if (el) el.textContent = text;
    };
    bootStage('正在登录…');
    const business = await bootstrapBusiness();
    if (!business) return;
    const session = new RoomSession(business.user.id, business.invite);
    // 世界场景（/solo）= 离线单人漫游：RoomSession 保持未连接（publish/command
    // 全部 no-op），不进入房间也不同步其他玩家；与「加入场景」的联机房间分开。
    const solo = business.solo === true;
    const canvas = document.getElementById('app') as HTMLCanvasElement;
    bootStage('正在创建渲染设备…');
    const { app, cameraRoot, camera } = await createApp(canvas);

    const ui = new UiManager();
    const sceneManager = new SceneManager(app);
    const remotes = new RemotePlayers(app, camera, business.user.id,
        // Local body used for nameplate occlusion: hidden avatars (first person /
        // camera pulled in) must not block remote nameplates.
        () => avatar.visible ? { position: rig.root.getPosition(), feetDrop: avatar.feetDrop } : null,
        // 场景碰撞体（与视频面板遮挡代理同源几何）：墙/建筑挡住头牌时隐藏。
        () => sceneManager.current?.collision ?? null);
    session.onSnapshot = snapshot => { remotes.snapshot(snapshot); currentRoomId = snapshot.room.id; const me = snapshot.players.find(p => p.profile.id === business.user.id); if (me) acceptAction(me.action, me.state.membershipEpoch); if (me) void voice.setAdminMuted(me.profile.muted === true); if (snapshot.room.id !== voiceRoom) joinVoice(snapshot.room.id); };
    session.onStates = batch => remotes.states(batch);
    // ── 房间动态物体（主持人 GLB 摆件，P3a）──
    // 服务端权威：增量走 room:objects 事件（spawn/remove），全量走 HTTP
    // GET /hub/rooms/:id/objects（规避 socket 单消息 16KB 上限）。本地按
    // dynamicIds 集合 diff 应用；场景卸载（换房/传送）时 SceneObjectManager
    // 整体销毁，onLoad 后重新拉全量恢复。
    const dynamicIds = new Set<string>();
    let currentRoomId = '';
    const applyDynamicObjects = (objects: unknown[]) => {
        const inst = sceneManager.current;
        if (!inst?.objects) return;
        const list = objects as { id: string }[];
        const wanted = new Set(list.map(o => o.id));
        for (const o of list) if (!dynamicIds.has(o.id)) { dynamicIds.add(o.id); inst.objects.add(o as never); }
        for (const id of [...dynamicIds]) if (!wanted.has(id)) { dynamicIds.delete(id); inst.objects.remove(id); }
    };
    const pullRoomObjects = async (roomId: string) => {
        if (solo || !session.online) return;
        try {
            const data = await business.api<{ revision: number; objects: unknown[] }>(`/hub/rooms/${encodeURIComponent(roomId)}/objects`);
            applyDynamicObjects(data.objects ?? []);
        } catch { /* 拉取失败静默：增量事件仍会驱动 */ }
    };
    const handleRoomObjectsEvent = (data: { action: string; object?: { id: string }; id?: string; transform?: { position: [number, number, number]; rotation: [number, number, number]; scale: [number, number, number] } }) => {
        const inst = sceneManager.current;
        if (data.action === 'spawn' && data.object) {
            if (inst?.objects && !dynamicIds.has(data.object.id)) { dynamicIds.add(data.object.id); inst.objects.add(data.object as never); }
        } else if (data.action === 'remove' && data.id) {
            dynamicIds.delete(data.id);
            inst?.objects.remove(data.id);
        } else if (data.action === 'move' && data.id && data.transform) {
            // Gizmo 拖拽结束同步：applyTransform 数据+实体一步到位（不重建 marker）。
            inst?.objects.applyTransform(data.id, data.transform.position, data.transform.rotation, data.transform.scale);
        }
    };
    session.onEvent = (name, data) => { remotes.event(name, data); if (name === 'player:action' && data.userId === business.user.id) acceptAction(data.action, data.membershipEpoch); if (name === 'player:muted' && data.userId === business.user.id) { void voice.setAdminMuted(data.muted === true); ui.showHint(data.muted === true ? '你已被主持人禁言' : '主持人已解除你的禁言', 5000); } if (name === 'player:moderator' && data.userId === business.user.id) worldUI.onModerationChanged(data.isModerator === true); if (name === 'room:objects') handleRoomObjectsEvent(data); if (name === 'room:kicked' || name === 'room:closed') { remotes.clear(); voiceRoom = ''; void voice.leave(); voiceControls.setJoined(false); } };
    // 声网 RTC 语音：与 socket.io 状态同步完全解耦，音频直连 SD-RTN。凭据由
    // 服务端按房间下发（频道人数上限沿用房间 maxParticipants），默认关麦。
    // agora SDK（~1.6MB/gzip~440KB）不进主包：LazyVoice 首次 join 才 import()。
    // 下载时机再由下方 SCENE_READY 门控推后 —— 场景可见 1.2s 后才开始，
    // 不与进场阻塞 LOD 块抢带宽；浏览器缓存后再次进房瞬时可用，solo 不下载。
    const voice = new LazyVoice();
    const voiceControls = new VoiceControls(voice);
    voice.onHint = (message) => ui.showHint(message);
    // voiceRoom 只在成功入频道后更新：传送时 onLoad 先于 portal:commit 触发，
    // 此时服务端仍算旧房间成员（凭据 403），靠下方快照对比在 commit 后自动重试。
    let voiceRoom = '';
    // 场景主体就绪前快照就可能触发 joinVoice：先记住想进的房间，就绪后再入会。
    let voiceSceneSettled = false;
    let voiceWantedRoom = '';
    const joinVoice = (roomId: string) => {
        if (solo || roomId === voiceRoom) return;
        if (!voiceSceneSettled) { voiceWantedRoom = roomId; return; }
        void business.api<VoiceCredentials>(`/hub/voice-credentials?roomId=${encodeURIComponent(roomId)}`)
            .then(creds => voice.join(creds))
            .then(() => { voiceRoom = roomId; voiceControls.setJoined(true); })
            .catch(() => voiceControls.setJoined(false)); // 语音不可用（未配置/未在房间）静默降级
    };
    // 首个场景就绪后放行语音：等 1.2s 让 full 阶段近场精化的头几个大块先走，
    // 然后下载 SDK（或命中缓存）并入会。之后传送换房走 joinVoice 直通（SDK 已在内存）。
    eventBus.on('SCENE_READY', () => {
        if (voiceSceneSettled) return;
        window.setTimeout(() => {
            voiceSceneSettled = true;
            if (voiceWantedRoom) { const room = voiceWantedRoom; voiceWantedRoom = ''; joinVoice(room); }
        }, 1200);
    });
    // 房间/传送状态（含「传送未完成：PORTAL_UNCONFIGURED」等失败原因）此前无人
    // 订阅而完全静默，绑定到 HUD hint 让玩家能看到传送为什么失败。
    session.onStatus = (message) => ui.showHint(message);
    // Release pointer lock whenever a popup opens so the mouse cursor is free
    // to click the close button.
    ui.requestUnlock = () => pointerLock.exit();

    // Pause all in-world scene videos when any popup (panel/media) opens, and
    // restore their prior play/pause state when the popup closes. Handles
    // multiple video screens: tracks which IDs were paused so the right ones
    // are resumed.
    let savedVideoIds: string[] = [];
    ui.onPopupOpen = () => {
        savedVideoIds = sceneManager.current?.objects.pauseAllVideoScreens() ?? [];
        // 弹窗（主菜单/控制台等）打开时结束主持人物体会话，避免双层光标交互。
        hostTools?.cancel();
    };
    ui.onPopupClose = () => {
        if (savedVideoIds.length) {
            sceneManager.current?.objects.resumeVideoScreens(savedVideoIds);
            savedVideoIds = [];
        }
    };

    // In-scene hub menu (⚙ 设置): 世界/创建/加入/角色/分享/公告/帮助/反馈,
    // backed by the authenticated /api/v1/hub endpoints. Room switching hands
    // control back to the standard join flow via a full page navigation.
    // 主持人动态物体工具会话（P3b：光标放置 + Gizmo 移动/旋转）：懒实例化，
    // 首次从控制台「物体」页签进入时才创建（Gizmo layer 不给普通会话常驻）。
    // 放置：光标瞄准 + 滚轮调距 + 左键确认，朝向自动面向玩家；移动：Gizmo
    // 拖拽松手上行完整变换（room:object-move），全员 setTransform 平滑应用。
    let hostTools: HostObjectTools | null = null;
    const getHostTools = () => hostTools ??= new HostObjectTools({
        app, camera, canvas,
        getCollision: () => sceneManager.current?.collision ?? null,
        getObjects: () => sceneManager.current?.objects ?? null,
        onSpawn: (model, pos) => {
            const camPos = camera.getPosition();
            // 物体正面朝向玩家（yaw：模型 +Z 指向相机）。
            const yaw = Math.atan2(camPos.x - pos[0], camPos.z - pos[2]) * 180 / Math.PI;
            void session.spawnRoomObject({
                type: 'model', name: model.name,
                transform: { position: pos, rotation: [0, yaw, 0], scale: [1, 1, 1] },
                marker: { kind: 'glb', url: model.modelUrl, scale: 1 }
            })
                .then(() => ui.showHint(`已放置「${model.name}」`))
                .catch((err) => ui.showHint(`放置失败：${err instanceof Error ? err.message : err}`, 5000));
        },
        onMove: (id, transform) => {
            void session.moveRoomObject(id, transform)
                .catch((err) => ui.showHint(`同步失败：${err instanceof Error ? err.message : err}`, 5000));
        },
        onHint: (message) => ui.showHint(message, 5000),
        requestUnlock: () => pointerLock.exit(),
        onLook: (mx, my) => input.frame.appendRotate(mx * input.mouseSensitivity, my * input.mouseSensitivity, 0)
    });
    // 视频控制按钮的主持人门控转发：hud 在 worldUI 之后创建，而 admin 身份
    // 在构造期同步回调（会踩 const TDZ），先落到转发函数、hud 就绪后重绑。
    let syncVideoGate = (_granted: boolean): void => { /* hud 未就绪，忽略（默认允许） */ };
    const worldUI = new WorldUI(ui, {
        user: business.user,
        api: business.api,
        currentRoom: business.invite,
        onJoinRoom: (target) => {
            location.assign('/join?' + new URLSearchParams({
                roomId: target.roomId, sceneId: target.sceneId, invite: target.invite
            }));
        },
        // 世界场景点击卡片 → 离线单人漫游（独立 /solo 入口，不进联机房间）。
        onEnterScene: (sceneId) => {
            location.assign('/solo?' + new URLSearchParams({ sceneId }));
        },
        // 角色面板切换形象 → 立即重建本地第三人称模型（无需重进场景）
        onAvatarChange: (avatarId) => {
            void business.api<{ id: string; modelUrl: string; config: Record<string, unknown> }[]>('/hub/avatars')
                .then(async (list) => {
                    const found = list.find((a) => a.id === avatarId);
                    if (found) await applyAvatar({ ...found, revision: 1, enabled: true } as Avatar);
                })
                .catch(() => ui.showHint('新形象加载失败'));
        },
        // 主持人「物体」页签：放置 = 光标射线放置会话（P3b）；移动 = Gizmo
        // 编辑会话；删除走已放置列表。全部经 socket 命令，全员实时同步。
        onPlaceObject: (model) => {
            if (solo) { ui.showHint('离线漫游暂不支持物体放置', 5000); return; }
            getHostTools().beginPlace(model);
        },
        onEditObject: (id) => { getHostTools().editObject(id); },
        onRemoveObject: (id) => session.removeRoomObject(id),
        // 视频控制仅主持人（房主/口令授权/管理员）显示；solo 离线漫游无
        // 主持人概念（无房间不查询、回调不触发），保持默认可见。
        onModeratorStateChange: (granted) => syncVideoGate(granted)
    });

    // --- Persistent player rig + controllers (reused across scenes) ---
    const rig = new PlayerRig(cameraRoot, camera);
    const fly = new FirstPersonController();
    const walk = new CharacterController();
    const third = new ThirdPersonController();
    // Avatar loads asynchronously; a placeholder capsule is used if the GLB is
    // absent, so the third-person system is testable before player.glb exists.
    let avatar = new AvatarController(app, rig, avatarConfig);
    avatar.setVisible(false); // first-person by default; shown when entering third-person.
    void avatar.load();
    // 隐身模式（基础信息 hidden）语义补全：远端 RemotePlayers 对 hidden 玩家隐藏
    // 模型+名牌，本端第三人称此前仍能看到自己——这里统一为「都看不到」。每帧同步：
    // 视角切换/进房 onEnter 内部会把模型设回可见，必须按「实际可见性 vs 期望值」
    // 纠正（缓存上次命令值会漏掉被 onEnter 覆盖的情况）；PATCH /me 成功回写
    // business.user 后下一帧立即生效。avatar 换形象时被整体重建（applyAvatar），
    // 闭包读 let 引用自动指向新实例。
    const syncSelfAvatarVisible = () => {
        const visible = viewMode === 'third' && (business.user as { hidden?: boolean }).hidden !== true;
        if (avatar.visible !== visible) avatar.setVisible(visible);
    };
    let anim = new AnimationStateMachine(avatar);
    let localAction: ActionState | null = null;
    let actionEpoch = -1;
    let renderedActionSeq = -1;
    function acceptAction(action: ActionState, epoch: number) {
        if (epoch !== session.admission?.membershipEpoch) return;
        if (epoch === actionEpoch && localAction && action.actionSeq < localAction.actionSeq) return;
        if (epoch !== actionEpoch) renderedActionSeq = -1;
        actionEpoch = epoch; localAction = action;
    }
    function updateNetworkAction() {
        // 离线单人漫游：动作由 HUD 本地播放（AnimationStateMachine 自带打断/
        // 播完回 idle 逻辑），不走网络状态机，只需维持动作期间的 root-motion 锁。
        if (solo) {
            if (anim.manualActionActive) avatar.lockRootMotion('action', anim.manualActionName);
            return;
        }
        const action = localAction;
        const elapsed = action ? session.serverTime - action.startedAt : 0;
        if (!action?.actionId || actionEpoch !== session.admission?.membershipEpoch || (!action.loop && elapsed >= action.duration)) {
            if (anim.manualActionActive) anim.reset(); return;
        }
        if (elapsed < 0) return;
        if (renderedActionSeq !== action.actionSeq || anim.manualActionName !== action.actionId) {
            if (!anim.playAction(action.actionId)) return;
            const layer = avatar.animLayer, duration = layer?.activeStateDuration ?? 0;
            if (layer && duration > 0) layer.activeStateCurrentTime = action.loop ? elapsed / 1000 % duration : Math.min(duration * 0.999, elapsed / 1000);
            renderedActionSeq = action.actionSeq;
        }
        avatar.lockRootMotion('action', action.actionId);
    }
    third.avatar = avatar;
    third.anim = anim;
    const state = new CameraState();
    state.fov = globalConfig.fov;

    let active: CameraController = fly;
    let currentCollision: Collision | null = null;
    /** 'first' = fly/walk first-person; 'third' = orbiting follow camera. */
    let viewMode: 'first' | 'third' = 'first';
    /** Movement semantics shared by first- and third-person views. */
    let moveMode: 'fly' | 'walk' = 'fly';

    // --- Input + pointer lock ---
    const input = new InputController(canvas);
    input.mouseSensitivity = globalConfig.mouseSensitivity;
    const pointerLock = new PointerLockManager(canvas);
    const crosshair = document.getElementById('crosshair');
    if (crosshair) crosshair.classList.add('reticle-hidden');
    const fpsMeter = document.getElementById('fps-meter');
    // 手机没有 F8：点按右上角帧率指示即可轮换 GSplat 调试（同桌面 F8：
    // LOD 着色 → 八叉树 AABB → 关闭）。调试开启时指示器描边高亮并带模式短标。
    fpsMeter?.addEventListener('click', () => cycleGsplatDebug());
    const renderGateApp = app as unknown as RenderGateApp;
    const lastRenderState = new CameraState();
    let lastRenderStateReady = false;
    let mobileRenderIdleTime = 0;
    let fpsAccum = 0;
    let fpsFrames = 0;

    // --- Mouse cursor tracking for click interactions (non-editor mode) ---
    // In game mode, clicks use the mouse cursor position (not screen center)
    // to build a world-space ray for hotspot/object picking.
    let _mouseX = 0;
    let _mouseY = 0;
    let _mouseValid = false;
    window.addEventListener('mousemove', (e) => {
        _mouseX = e.clientX;
        _mouseY = e.clientY;
        _mouseValid = true;
    }, { passive: true });

    async function requestMobileLandscape() {
        try {
            await document.documentElement.requestFullscreen?.();
            const orientation = screen.orientation as ScreenOrientation & { lock?: (orientation: 'landscape') => Promise<void> };
            await orientation.lock?.('landscape');
        } catch (err) {
            console.warn('[mobile] landscape lock unavailable:', err);
        }
    }
    // 原生横屏是键盘从「场景底部」弹出的唯一途径：首次失败（用户没理会全屏
    // 请求 / WebView 不支持）不代表永远失败，虚拟横屏期间按 3 秒节流重试，
    // 锁定成功后 app-root 切到 native-landscape 类即自动停止。iOS Safari 无
    // orientation.lock API 恒为虚拟横屏，由下面的竖屏代理输入条兜底。
    let landscapeRetryAt = 0;
    window.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'touch') return;
        if (!document.getElementById('app-root')?.classList.contains('mobile-virtual-landscape')) return;
        if (Date.now() - landscapeRetryAt < 3000) return;
        landscapeRetryAt = Date.now();
        void requestMobileLandscape();
    }, { passive: true });
    // 虚拟横屏下系统键盘只能从物理底部（=场景侧边）弹出：文本输入改走竖屏
    // 代理输入条（正立、紧贴键盘上沿，实时回写真实输入框），见 MobileKeyboardSheet。
    installPortraitKeyboardSheet();

    function triggerSceneClickInteraction(): boolean {
        // Use mouse/touch position to build a world-space ray (instead of
        // screen-center / crosshair forward). Falls back to camera forward
        // when no mouse/touch position has been recorded yet.
        // On desktop with pointer lock active, clientX/clientY are frozen, so
        // use camera forward (center of screen) instead of stale mouse coords.
        if (!_mouseValid || pointerLock.locked) {
            const hotspotHit = sceneManager.current?.hotspots.onClick(state) ?? null;
            const objectHit = sceneManager.current?.objects.onClick(state) ?? null;
            return Boolean(hotspotHit || objectHit);
        }
        // PlayCanvas screenToWorld uses canvas.getBoundingClientRect() internally,
        // which returns the visual bounding box (already accounts for CSS transforms
        // like the mobile virtual-landscape rotate). So we can pass raw clientX/clientY.
        const origin = camera.getWorldTransform().getTranslation();
        const far = new Vec3();
        camera.camera!.screenToWorld(_mouseX, _mouseY, 1000, far);
        const dir = new Vec3(far.x - origin.x, far.y - origin.y, far.z - origin.z).normalize();
        const hotspotHit = sceneManager.current?.hotspots.onClickRay(origin.x, origin.y, origin.z, dir.x, dir.y, dir.z) ?? null;
        const objectHit = sceneManager.current?.objects.onClickRay(origin.x, origin.y, origin.z, dir.x, dir.y, dir.z) ?? null;
        return Boolean(hotspotHit || objectHit);
    }

    // Fixed bottom-right gameplay controls. Hidden in editor.
    const hud = new HudControls(input, {
        onToggleView: () => toggleThirdPerson(),
        onToggleMoveMode: () => toggleMoveMode(),
        onAvatarAction: (name) => {
            if (!session.online) {
                // 离线单人漫游：动作直接本地播放；联机房间未就绪时忽略。
                if (solo) anim.playAction(name);
                return;
            }
            const epoch = session.admission!.membershipEpoch;
            void session.action(name).then(action => acceptAction(action, epoch)).catch(e => ui.showHint(e.message));
        },
        onVideoShow: () => sceneManager.current?.objects.showFocusedVideoScreen(state),
        onVideoHide: () => sceneManager.current?.objects.hideFocusedVideoScreen(state),
        onVideoPlayPause: () => sceneManager.current?.objects.toggleVideoPlayback(state),
        onVideoMuteToggle: () => sceneManager.current?.objects.toggleVideoMuted(state),
        onVideoSeek: (time) => sceneManager.current?.objects.seekVideo(state, time),
        getViewMode: () => viewMode,
        getMoveMode: () => moveMode,
        getVideoState: () => sceneManager.current?.objects.getVideoControlState(state) ?? null,
        actionAnims: avatarConfig.actionAnims
    });
    // HUD 构造时即为 hidden；场景就绪（switchTo 内）后才显示，
    // 登录/加载阶段不出现任何玩法按钮。
    // hud 就绪后接管视频控制门控（此前 admin 同步回调已被默认值兜住）。
    syncVideoGate = (granted) => hud.setVideoControlsAllowed(granted);

    // --- Manifest cache + registry ---
    const manifestCache = new Map<string, SceneManifest>();
    bootStage('正在加载场景清单…');
    let registry = await fetchRegistry(globalConfig.registryUrl);
    const sceneIds = () => registry.scenes.map((s) => s.id);

    async function getManifest(sceneId: string): Promise<SceneManifest> {
        const cached = manifestCache.get(sceneId);
        if (cached) return cached;
        const manifest = await fetchSceneManifest(`/assets/scenes/${sceneId}/scene.json`);
        manifestCache.set(sceneId, manifest);
        return manifest;
    }

    // --- Editor (Scene Builder): place/select/edit unified objects, export JSON ---
    const assetLib = new EditorAssetLibrary();
    bootStage('正在加载编辑器资源…');
    await assetLib.load();
    const editor = new SceneEditor(app, sceneManager, camera, state, sceneIds, assetLib, (t) => ui.showHint(t));
    // Save: persist via the editor, then drop the cached manifest so the next
    // visit (or a browser refresh) re-reads the freshly written scene.json.
    const saveCurrent = async () => {
        const msg = await editor.save();
        const id = sceneManager.current?.manifest.id;
        if (id) manifestCache.delete(id);
        // Surface the save outcome prominently so it's unmissable: a successful
        // PUT, a download fallback, or a validation failure each read differently.
        if (msg) ui.setStatus(msg);
    };

    const toolbar = new EditorToolbar(
        editor.state,
        assetLib,
        (assetId) => editor.beginPlace(assetId),
        async () => {
            editor.setSpawnFromCurrentPose();
            await saveCurrent();
        },
        saveCurrent,
        () => editor.export(),
        () => exitEditor()
    );
    editor.attachPanels(toolbar.propertyMount, toolbar.listMount);
    editor.onSelectChange = (id) => toolbar.setPlacingActive(id);

    // Snapshot of the movement/view mode taken when entering the editor, so
    // exitEditor() can restore it (enterEditor forces first-person fly).
    let preEditorViewMode: 'first' | 'third' | null = null;
    let preEditorMoveMode: 'fly' | 'walk' | null = null;
    // Saved fly altitude clamps; disabled while the editor is active so the
    // designer can free-fly to any height when placing objects.
    let preEditorFlyMaxHeight = 0;
    let preEditorThirdMaxHeight = 0;

    function enterEditor() {
        // 编辑器门控：仅主持人（房主/口令授权/管理员）且场景内无其他玩家可进
        // ——scene.json 是服务端共享的场景级资源，多人在线期间禁改。编辑中途
        // 进人/权限收回由主循环每帧强退兜底。场景未就绪（加载/切场景/重置中）不响应。
        if (switching || resetPending || (!session.online && !solo)) return;
        if (!worldUI.isModerator) { ui.showHint('仅主持人可编辑场景'); return; }
        if (remotes.hasRemotePlayers) {
            ui.showHint('多人活动中不能编辑场景；请在管理后台配置空闲场景。');
            return;
        }
        if (editor.enabled) return;
        editor.toggle();
        toolbar.show();
        hud.setVisible(true);
        // Editor is cursor-driven: release the pointer lock so the cursor is
        // visible and the designer can click the toolbar/panels.
        if (pointerLock.locked) pointerLock.exit();
        // Force fly mode while editing so the designer can free-fly around the
        // scene to place/select objects without walk-mode collision constraints.
        // Snapshot the current mode first so exitEditor() can restore it.
        preEditorViewMode = viewMode;
        preEditorMoveMode = moveMode;
        setMode('fly');
        // Disable the fly altitude clamp while editing so the designer can
        // free-fly to any height.
        preEditorFlyMaxHeight = fly.flyMaxHeight;
        preEditorThirdMaxHeight = third.flyMaxHeight;
        fly.flyMaxHeight = Infinity;
        third.flyMaxHeight = Infinity;
    }
    function exitEditor() {
        if (!editor.enabled) return;
        editor.toggle();
        toolbar.hide();
        hud.setVisible(false);
        voiceControls.setVisible(true);
        // Restore the movement/view mode that was active before entering the
        // editor (enterEditor forces first-person fly).
        if (preEditorViewMode === 'third') {
            moveMode = preEditorMoveMode ?? 'fly';
            syncMoveMode();
            if (viewMode !== 'third') toggleThirdPerson();
        } else if (preEditorMoveMode) {
            setMode(preEditorMoveMode);
        }
        preEditorViewMode = null;
        preEditorMoveMode = null;
        // Restore the fly altitude clamp that was disabled in enterEditor().
        fly.flyMaxHeight = preEditorFlyMaxHeight;
        third.flyMaxHeight = preEditorThirdMaxHeight;
    }

    // --- Switching guard: true while a scene transition is in flight ---
    let switching = false;
    /** 朝向跟随滚动预载的停止器：切场景时停掉旧实例的定时器。 */
    let followPreloadStop: { stop(): void } | null = null;
    // 每场景资源优先级门：进场后带宽分配次序 碰撞 GLB → 形象 GLB → 精细
    // LOD 块（follow/full/near）。碰撞是行走模式硬依赖（laomendong2 15.7MB
    // 单文件，与多块预载共享 6 连接池时只分到 ~1/7 带宽），形象是他人视角的
    // 门面（19-24MB），都排在大流量的精化块之前。switchTo 尾部创建并挂到
    // scenePreloadGates：碰撞门由 loadDeferredAssets 的碰撞回调释放；形象门
    // 由形象串行链（queueAvatarYield/solo 路径）在形象落地或失败后释放；
    // 每级门带超时兜底，慢网络/无形象请求时不互相死等。
    interface ScenePreloadGates {
        collision: Promise<void>;
        releaseCollision: () => void;
        avatar: Promise<void>;
        releaseAvatar: () => void;
    }
    let scenePreloadGates: ScenePreloadGates | null = null;
    // 碰撞优先窗口：超时后无论碰撞是否到货都放行下一级，碰撞继续后台下载，
    // 避免慢网络下后续资源被无限拖延。
    const COLLISION_PRIORITY_TIMEOUT_MS = 15000;
    // 形象优先窗口：形象 GLB 下载/应用超过这个时长后放行精细 LOD（并发期
    // 形象仍会继续下载，只是不再独占带宽）。
    const AVATAR_PRIORITY_TIMEOUT_MS = 15000;
    // 优先级窗口内的分块流送并发：门只拦得住 follow/full/near 预载，引擎
    // 渲染驱动的按需流送不走门——进场时以 coarse 阶段遗留的 12 路并发排队
    // 精细块，碰撞/形象这类单文件大下载按连接公平分享只能分到 ~1/13 带宽
    // （HTTP/2 单连接多路复用下更甚）。窗口内钳到 1 路，碰撞+形象落地后
    // 恢复后台并发。
    const PRIORITY_WINDOW_CHUNK_CONCURRENCY = 1;

    function syncMoveMode(): void {
        input.mode = moveMode;
        third.moveMode = moveMode;
    }

    function emitModeChange(): void {
        eventBus.emit({
            type: 'MODE_CHANGE',
            mode: viewMode === 'third' ? 'third' : moveMode,
            viewMode,
            moveMode
        });
    }

    function firstPersonController(): CameraController {
        return moveMode === 'walk' ? walk : fly;
    }

    /** Re-seat the player into the just-loaded scene. */
    function applySceneToPlayer(
        manifest: SceneManifest,
        collision: Collision | null,
        spawnOverride?: [number, number, number],
        anglesOverride?: [number, number]
    ) {
        currentCollision = collision;
        fly.collision = collision;
        walk.collision = collision;
        walk.fallLimit = manifest.fallLimit;
        third.collision = collision;
        third.fallLimit = manifest.fallLimit;

        const spawn = spawnOverride ?? manifest.spawn;
        const angles = anglesOverride ?? manifest.spawnAngles;
        state.position.set(spawn[0], spawn[1], spawn[2]);
        state.angles.set(angles[0], angles[1], 0);
        state.cameraOffset.set(0, 0, 0);
        state.fov = globalConfig.fov;

        // Default movement mode for this scene (walk needs collision).
        const wantWalk = collision != null && manifest.defaultMode === 'walk';
        moveMode = wantWalk ? 'walk' : 'fly';
        syncMoveMode();

        // Re-activate the controller matching the current view. Third-person
        // fly can run without collision; third-person walk falls back if needed.
        if (viewMode === 'third' && (moveMode === 'fly' || collision != null)) {
            active = third;
            avatar.setVisible(true);
        } else {
            viewMode = 'first';
            active = firstPersonController();
            avatar.setVisible(false);
            avatar.clearFlightPose();
            state.cameraOffset.set(0, 0, 0);
        }
        active.onEnter(state);
        emitModeChange();
        hud.sync();
    }

    function installSceneCollision(collision: Collision | null): void {
        const hadCollision = currentCollision !== null;
        currentCollision = collision;
        fly.collision = collision;
        walk.collision = collision;
        third.collision = collision;
        if (
            !hadCollision &&
            collision &&
            sceneManager.current?.manifest.defaultMode === 'walk' &&
            viewMode === 'first' &&
            input.mode === 'fly' &&
            moveMode === 'fly'
        ) {
            // 仅当玩家仍停在出生点附近才自动升级行走：碰撞迟到时进场先飞，若
            // 玩家已主动飞离，中途切行走会从空中坠落——保持飞行，需要时 V 键
            // 手动切换（碰撞已就绪，setMode 不再拒绝）。
            const s = sceneManager.current.manifest.spawn;
            const dx = state.position.x - s[0], dy = state.position.y - s[1], dz = state.position.z - s[2];
            if (dx * dx + dy * dy + dz * dz <= 16) {
                setMode('walk');
                return;
            }
            // 玩家已飞离出生点：不强制切行走（半空坠落），提示 V 键可手切。
            ui.showHint('碰撞已就绪 · 按 V 切换行走');
        }
        hud.sync();
    }

    /** Switch to a first-person mode (fly/walk). No-op if already there. */
    function setMode(mode: 'fly' | 'walk') {
        if (viewMode === 'first' && moveMode === mode && input.mode === mode) return;
        if (mode === 'walk' && !currentCollision) {
            ui.setStatus('无碰撞数据，无法切换到行走模式');
            return;
        }
        active.onExit(state);
        state.cameraOffset.set(0, 0, 0);
        moveMode = mode;
        syncMoveMode();
        active = firstPersonController();
        viewMode = 'first';
        avatar.setVisible(false);
        avatar.clearFlightPose();
        active.onEnter(state);
        emitModeChange();
        hud.sync();
    }

    function setMoveMode(mode: 'fly' | 'walk'): void {
        if (moveMode === mode) return;
        if (mode === 'walk' && !currentCollision) {
            ui.setStatus('No collision data, cannot switch to walk mode');
            return;
        }
        active.onExit(state);
        moveMode = mode;
        syncMoveMode();
        active = viewMode === 'third' ? third : firstPersonController();
        if (viewMode === 'first') {
            avatar.setVisible(false);
            avatar.clearFlightPose();
            state.cameraOffset.set(0, 0, 0);
        } else {
            avatar.setVisible(true);
        }
        active.onEnter(state);
        if (viewMode === 'third') {
            anim.reset();
            avatar.lockRootMotion();
            rig.apply(state);
        }
        emitModeChange();
        hud.sync();
    }

    function toggleMoveMode() {
        setMoveMode(moveMode === 'walk' ? 'fly' : 'walk');
    }

    /** Toggle third-person on/off. Returns to the last first-person mode. */
    function toggleThirdPerson() {
        if (viewMode === 'third') {
            setMode(moveMode);
            ui.showHint(`第一人称 · ${moveMode === 'walk' ? '行走' : '飞行'} · V 切换`);
            return;
        }
        if (moveMode === 'walk' && !currentCollision) {
            ui.setStatus('无碰撞数据，无法切换到第三人称');
            return;
        }
        active.onExit(state);
        active = third;
        viewMode = 'third';
        syncMoveMode();
        active.onEnter(state); // shows avatar + snaps facing
        anim.reset();
        avatar.lockRootMotion();
        rig.apply(state);
        emitModeChange();
        hud.sync();
        ui.showHint(moveMode === 'fly'
            ? '第三人称飞行 · WASD/左摇杆沿镜头飞行 · E/Q 升降 · 右摇杆/鼠标控制俯仰 · 滚轮缩放'
            : '第三人称漫游 · WASD/左摇杆移动 · Shift 跑 · Space 跳 · 右摇杆/鼠标旋转视角 · 滚轮缩放');
    }

    /** Diagnostic: cycle the global gsplat debug mode to see which LOD renders. */
    const gsplatDebugCycle = [
        { mode: GSPLAT_DEBUG_LOD, label: 'LOD 着色（按 LOD 级别上色）' },
        { mode: GSPLAT_DEBUG_NODE_AABBS, label: '八叉树节点 AABB（看哪些分块已加载）' },
        { mode: GSPLAT_DEBUG_NONE, label: '关闭' }
    ];
    let gsplatDebugIdx = 2; // start off
    const gsplatDebugShort = ['LOD', 'AABB', ''];
    function cycleGsplatDebug() {
        gsplatDebugIdx = (gsplatDebugIdx + 1) % gsplatDebugCycle.length;
        const cur = gsplatDebugCycle[gsplatDebugIdx];
        app.scene.gsplat.debug = cur.mode;
        const meter = document.getElementById('fps-meter');
        if (meter) {
            meter.classList.toggle('gsplat-debug-on', cur.mode !== GSPLAT_DEBUG_NONE);
            meter.title = 'GSplat 调试（点按切换）：' + cur.label;
        }
        ui.showHint(`GSplat 调试：${cur.label}`);
        console.log(`[gsplat-debug] mode=${cur.mode} (${cur.label})`);
    }

    function requestRenderFrame(): void {
        renderGateApp.renderNextFrame = true;
    }

    function captureRenderState(): void {
        lastRenderState.copy(state);
        lastRenderStateReady = true;
    }

    function cameraStateChangedForRender(): boolean {
        if (!lastRenderStateReady) return true;
        const dx = state.position.x - lastRenderState.position.x;
        const dy = state.position.y - lastRenderState.position.y;
        const dz = state.position.z - lastRenderState.position.z;
        const ax = state.angles.x - lastRenderState.angles.x;
        const ay = state.angles.y - lastRenderState.angles.y;
        const az = state.angles.z - lastRenderState.angles.z;
        const ox = state.cameraOffset.x - lastRenderState.cameraOffset.x;
        const oy = state.cameraOffset.y - lastRenderState.cameraOffset.y;
        const oz = state.cameraOffset.z - lastRenderState.cameraOffset.z;
        return dx * dx + dy * dy + dz * dz > MOBILE_RENDER_POS_EPS_SQ ||
            ox * ox + oy * oy + oz * oz > MOBILE_RENDER_POS_EPS_SQ ||
            ax * ax + ay * ay + az * az > MOBILE_RENDER_ANGLE_EPS_SQ ||
            state.fov !== lastRenderState.fov;
    }

    function updateMobileRenderGate(dt: number, videoActive: boolean): void {
        if (!isMobileRuntime()) {
            requestRenderFrame();
            return;
        }

        const cameraChanged = cameraStateChangedForRender();
        if (cameraChanged || videoActive || editor.enabled) {
            mobileRenderIdleTime = 0;
            captureRenderState();
            requestRenderFrame();
            return;
        }

        mobileRenderIdleTime += dt;
        renderGateApp.renderNextFrame = mobileRenderIdleTime < MOBILE_RENDER_IDLE_SECONDS;
    }

    /** Drive a full scene transition with fade. */
    async function switchTo(
        sceneId: string,
        spawnOverride?: [number, number, number],
        anglesOverride?: [number, number]
    ) {
        if (switching) return;
        if (sceneManager.current?.manifest.id === sceneId && sceneManager.current.asset) {
            applySceneToPlayer(sceneManager.current.manifest, sceneManager.current.collision, spawnOverride, anglesOverride);
            return;
        }
        switching = true;
        followPreloadStop?.stop();
        followPreloadStop = null;
        ui.hidePanel();
        pointerLock.exit();
        // Show the JS-controlled loader immediately with the scene-specific text
        // (matches LOADER_TEXT_BY_SCENE in UiManager) instead of leaving a black
        // gap while fadeOut + manifest fetch run.
        eventBus.emit({ type: 'SCENE_LOAD_START', name: sceneId, progress: 0 });
        await ui.fadeOut();

        if (sceneManager.current) sceneManager.unloadScene(sceneManager.current);

        let manifest: SceneManifest;
        let instance;
        try {
            manifest = await getManifest(sceneId);
            instance = await sceneManager.loadScene(manifest);
            if (!instance.entity || !instance.asset) throw new Error('高斯场景资源未加载成功');
            // 碰撞不再挡进场（原先这里 await loadCollision：15.7MB GLB 下载 +
            // 87 万三角形 BVH 全算进 loading）。loadScene 已不装碰撞，进场后由
            // loadDeferredAssets 后装、installSceneCollision 迟到升级。
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            eventBus.emit({ type: 'LOAD_ERROR', message: `场景切换失败：${msg}` });
            await ui.fadeIn();
            switching = false;
            throw err;
        }

        ui.bindScene(manifest.hotspots, manifest.triggerZones);
        applySceneToPlayer(manifest, instance.collision, spawnOverride, anglesOverride);
        if (editor.enabled) editor.sceneChanged();

        // Preload only low-detail scene-wide coverage before fading in. The
        // normal near-detail preload runs in the background after entry.
        try {
            if (instance.entity && instance.asset) {
                await preloadGaussianScene(
                    { entity: instance.entity, asset: instance.asset },
                    state.position,
                    (frac) => eventBus.emit({ type: 'SCENE_LOAD_START', name: manifest.name, progress: frac }),
                    { mobileLowQuality: isMobileRuntime(), preloadStage: 'coarse' }
                );
            }
        } catch (err) {
            console.error('[scene] coarse gsplat preload failed:', err);
            if (isMobileRuntime()) {
                ui.hideLoader();
                eventBus.emit({
                    type: 'LOAD_ERROR',
                    message: `低清高斯场景加载超时，已停止进入场景以避免黑屏。${err instanceof Error ? err.message : String(err)}`
                });
                await ui.fadeIn();
                switching = false;
                throw err;
            }
        }
        // Now the first view is resident — signal ready (sets bar to 100% and
        // lets the loader hide).
        eventBus.emit({ type: 'SCENE_READY', name: manifest.name });

        // Phase 4: log live asset count so memory unload can be verified by hand.
        console.log(`[scene] "${manifest.name}" ready — live assets: ${sceneManager.assetCount()}`);

        await ui.fadeIn();
        switching = false;
        // 场景就绪后才显示 HUD、FPS 与 ⚙ 设置按钮（登录/加载阶段全部隐藏）。
        hud.setVisible(false);
        voiceControls.setVisible(true);
        fpsMeter?.classList.remove('hidden');
        worldUI.revealSettingsButton();
        // 资源优先级门（桌面与手机统一）：碰撞 GLB → 形象 GLB → 精细 LOD 预载
        // 依次放行。碰撞任务不压延迟——进场瞬间 coarse 预载刚结束、连接池全
        // 空闲，此时启动能吃满带宽；coarse 基础场景在 loading 阶段已就绪，等
        // 待期画面有内容、玩家可飞行移动，不空等。
        const mkGate = (timeoutMs: number, label: string) => {
            let release: () => void = () => { };
            const gate = new Promise<void>((resolve) => { release = resolve; });
            const timer = window.setTimeout(() => {
                console.info(`[scene] ${label} gate timeout — releasing next stage`);
                release();
            }, timeoutMs);
            void gate.then(() => window.clearTimeout(timer));
            return { gate, release };
        };
        const collisionGate = mkGate(COLLISION_PRIORITY_TIMEOUT_MS, 'collision');
        const avatarGate = mkGate(AVATAR_PRIORITY_TIMEOUT_MS, 'avatar');
        scenePreloadGates = {
            collision: collisionGate.gate,
            releaseCollision: collisionGate.release,
            avatar: avatarGate.gate,
            releaseAvatar: avatarGate.release
        };
        /** 碰撞+形象都落地（或超时兜底）后：精细 LOD 预载的统一起点。 */
        const afterAvatar = Promise.all([collisionGate.gate, avatarGate.gate]).then(() => undefined);
        // 带宽保护：优先级窗口内把引擎按需分块流送钳到 1 路（见常量注释），
        // 窗口结束恢复后台并发（桌面 full 预载启动时也会设 6，change 守卫下
        // 双保险不重复）。场景已切走则不碰新场景的加载器。
        if (instance.entity && instance.asset) {
            const chunkHandle = { entity: instance.entity, asset: instance.asset };
            setGaussianChunkConcurrency(chunkHandle, PRIORITY_WINDOW_CHUNK_CONCURRENCY, 'priority-window');
            void afterAvatar.then(() => {
                if (sceneManager.current !== instance) return;
                setGaussianChunkConcurrency(chunkHandle, BACKGROUND_CHUNK_LOAD_CONCURRENCY, 'priority-window-end');
            });
        }
        // 碰撞/遮挡体后装：loading 只等可见内容（gsplat coarse 块），collision
        // GLB 下载 + BVH 构建转后台；进场先飞模式，碰撞到货后
        // installSceneCollision 迟到升级（walk 默认场景自动切行走）。
        window.setTimeout(() => sceneManager.loadDeferredAssets(instance, (collision) => {
            installSceneCollision(collision);
            console.info('[scene] collision settled — avatar next, fine LOD preloads wait');
            collisionGate.release();
        }), 0);

        // 朝向跟随滚动预载：进场预载全部围绕出生点一次性排队，走动/转身后
        // 新朝向区域只剩引擎「看到才请求」的按需加载，粗档要等下载完才精化
        // ——这就是「LOD 不跟着朝向刷新」的体感来源。此定时器跟随相机，把
        // 镜头前方扇区分环先 ensure 到位（桌面 30m 单档 LOD0；手机按距离 LOD1/2/3，
        // 侧后方随后补齐；限量入队不淹没 FIFO。渲染选档仍由引擎按节点投影
        // 尺寸+预算决定（同距离同档、视野外不渲染），这里只解决数据先到位。
        // 挂在碰撞+形象门后：先保行走模式与形象落地，再做视觉精化。
        const startFollowPreloadForInstance = () => {
            if (sceneManager.current !== instance) return;
            if (instance.entity && instance.asset) {
                followPreloadStop = startFollowPreload(
                    { entity: instance.entity, asset: instance.asset },
                    camera,
                    isMobileRuntime()
                        ? { mobileLowQuality: true, rings: MOBILE_NEAR_PRELOAD_RINGS }
                        : { mobileLowQuality: false, lod: 0, radius: 30 }
                );
            }
        };
        void afterAvatar.then(startFollowPreloadForInstance);
        // Mobile: the desktop 'full' background preload (near LOD0 + coarsest
        // for every node) OOM-killed large scenes (meihuashan 781MB/125
        // chunks), so mobile never runs it. Instead a bounded 'near' preload
        // sharpens just the spawn area a moment after entry: the cap-tier
        // (LOD1) chunks within MOBILE_NEAR_PRELOAD_RADIUS_M meters. Beyond that
        // radius the GSplat streaming system loads chunks on demand.
        const backgroundPreload = instance.entity && instance.asset && !isMobileRuntime();
        if (backgroundPreload) {
            const entity = instance.entity!;
            const asset = instance.asset!;
            // full 精化预载：碰撞+形象之后的第三级（带宽次序见 scenePreloadGates
            // 注释）。场景已切走则不再启动（实体/资产已卸载）。
            void afterAvatar.then(() => {
                window.setTimeout(() => {
                    if (sceneManager.current !== instance) return;
                    void preloadGaussianScene(
                        { entity, asset },
                        state.position,
                        undefined,
                        { mobileLowQuality: false, preloadStage: 'full' }
                    ).catch((err) => {
                        console.error('[scene] background gsplat preload failed:', err);
                    });
                }, 0);
            });
        }
        if (isMobileRuntime() && instance.entity && instance.asset) {
            const entity = instance.entity;
            const asset = instance.asset;
            window.setTimeout(() => {
                void afterAvatar.then(() => {
                    if (sceneManager.current !== instance) return;
                    console.info('[scene] mobile starts background near gsplat preload');
                    void preloadGaussianScene(
                        { entity, asset },
                        state.position,
                        undefined,
                        {
                            mobileLowQuality: true,
                            preloadStage: 'near',
                            nearLod: MOBILE_NEAR_PRELOAD_LOD,
                            nearRadius: MOBILE_NEAR_PRELOAD_RADIUS_M
                        }
                    ).catch((err) => {
                        console.error('[scene] mobile near gsplat preload failed:', err);
                    });
                });
            }, MOBILE_NEAR_PRELOAD_DELAY_MS);
        }
        ui.showHint(`已进入「${manifest.name}」· WASD 移动 · V 切换模式`);
    }

    // --- Portal → scene switch (honors requireConfirm) ---
    eventBus.on('PORTAL_ENTER', async (e) => {
        if (!e.id || (!session.online && !solo)) return;
        const obj = e.id ? sceneManager.current?.objects.get(e.id) : undefined;
        const portal = obj?.portal;
        if (portal?.requireConfirm) {
            const ok = await ui.showConfirm(portal.confirmText ?? `传送至「${e.targetScene}」？`);
            if (!ok) return;
        }
        if (session.online) {
            await session.transfer(e.id);
        } else if (e.targetScene) {
            // 离线单人漫游：直接本地切换到传送门目标场景。
            await switchTo(e.targetScene, e.targetSpawn, e.targetAngles ? [e.targetAngles[0], e.targetAngles[1]] : undefined);
        }
    });

    // --- Object event action: switchScene / hideScene (other action types handled by UiManager) ---
    eventBus.on('OBJECT_TRIGGER', (e) => {
        const actions = e.actions ?? (e.action ? [e.action] : []);
        for (const a of actions) {
            if (a.type === 'switchScene' && a.payload.targetScene) {
                if (session.online) {
                    if (e.id) void session.transfer(e.id);
                } else if (solo) {
                    // 离线单人漫游：本地切换目标场景。
                    void switchTo(a.payload.targetScene, a.payload.targetSpawn, a.payload.targetAngles ? [a.payload.targetAngles[0], a.payload.targetAngles[1]] : undefined);
                }
            } else if (a.type === 'hideScene') {
                sceneManager.hideSceneContent();
            }
        }
    });

    // --- hideScene zone exit → restore hidden scene content ---
    eventBus.on('SCENE_VISIBILITY', (e) => {
        if (e.visible) sceneManager.restoreSceneContent();
    });

    // --- Dismiss error screen on click (allows continued exploration) ---
    document.getElementById('error-screen')!.addEventListener('click', () => ui.hideError());

    // --- Click/tap: lock pointer on desktop, interact directly on mobile taps ---
    let touchTapStart: { pointerId: number; x: number; y: number; t: number } | null = null;
    canvas.addEventListener('pointerdown', (e) => {
        if (switching || editor.enabled) return;
        if (e.pointerType !== 'touch') return;
        touchTapStart = { pointerId: e.pointerId, x: e.clientX, y: e.clientY, t: performance.now() };
    });
    canvas.addEventListener('pointerup', (e) => {
        if (!touchTapStart || e.pointerId !== touchTapStart.pointerId) return;
        const start = touchTapStart;
        touchTapStart = null;
        if (switching || editor.enabled) return;
        const moved = Math.hypot(e.clientX - start.x, e.clientY - start.y);
        const elapsed = performance.now() - start.t;
        if (moved > TOUCH_TAP_MOVE_THRESHOLD || elapsed > TOUCH_TAP_MAX_MS) return;
        if (isMobileRuntime() && e.pointerType === 'touch') {
            _mouseX = e.clientX;
            _mouseY = e.clientY;
            _mouseValid = true;
            triggerSceneClickInteraction();
        }
    });
    canvas.addEventListener('pointercancel', () => { touchTapStart = null; });

    canvas.addEventListener('click', () => {
        if (switching) return;
        if (editor.enabled) return; // editor owns clicks while active (cursor-driven)
        if (hostTools?.active) return; // 主持人物体会话 owns clicks (cursor-driven)
        if (isMobileRuntime()) return;
        triggerSceneClickInteraction();
    });

    // --- Editor mouse: cursor aim + left-drag look + left-click place/select + wheel depth ---
    // Left-drag (hold + move beyond threshold) rotates the camera; a left-click
    // (down+up without dragging) places (if a prop is active) or selects.
    // While a gizmo handle is being dragged, the gizmo owns the pointer — skip
    // look/place/select so the camera stays put and the drag commits cleanly.
    let leftDown = false;
    let rightDown = false;
    let downX = 0;
    let downY = 0;
    let dragged = false;
    const DRAG_THRESHOLD = 4;
    const look = (e: MouseEvent) =>
        input.frame.appendRotate(
            e.movementX * input.mouseSensitivity,
            e.movementY * input.mouseSensitivity,
            0
        );
    canvas.addEventListener('mousemove', (e) => {
        if (!editor.enabled) return;
        editor.setMouse(e.clientX, e.clientY);
        if (editor.isGizmoDragging) return;
        if (leftDown) {
            if (!dragged && Math.hypot(e.clientX - downX, e.clientY - downY) > DRAG_THRESHOLD) dragged = true;
            if (dragged) look(e);
        } else if (rightDown) {
            look(e);
        }
    });
    canvas.addEventListener('mousedown', (e) => {
        if (!editor.enabled) return;
        if (editor.isGizmoDragging) return; // gizmo grabbed a handle on pointerdown
        if (e.button === 0) { leftDown = true; downX = e.clientX; downY = e.clientY; dragged = false; }
        else if (e.button === 2) rightDown = true;
    });
    window.addEventListener('mouseup', (e) => {
        if (!editor.enabled) return;
        if (e.button === 0) {
            if (leftDown && !dragged && !editor.isGizmoDragging) editor.onLeftClick();
            leftDown = false;
        } else if (e.button === 2) rightDown = false;
    });
    canvas.addEventListener('wheel', (e) => {
        if (hostTools?.active) return; // 会话自带滚轮监听（放置调距），跳过第三人称缩放
        if (editor.enabled) {
            editor.onWheel(e.deltaY);
            e.preventDefault();
        } else if (viewMode === 'third') {
            // Scroll up = closer, down = farther.
            third.adjustDistance(-e.deltaY * 0.002);
            e.preventDefault();
        }
    }, { passive: false });

    // --- Keyboard: mode toggle / reset / interact / close / editor ---
    window.addEventListener('keydown', (e) => {
        if (isEditableTarget(e.target)) return;
        // 主持人物体会话 owns keys（Esc 取消 / W·E 切换 Gizmo）；F2 等快捷键忽略。
        if (hostTools?.active) return;
        // Editor shortcuts. Text inputs own their keystrokes while focused.
        if (e.code === 'F2') {
            if (editor.enabled) exitEditor(); else enterEditor();
            return;
        }
        if (editor.enabled) {
            // Ctrl-key combos first (undo/redo/duplicate).
            if (e.ctrlKey || e.metaKey) {
                switch (e.code) {
                    case 'KeyZ': e.preventDefault(); editor.undo(); return;
                    case 'KeyY': e.preventDefault(); editor.redo(); return;
                    case 'KeyD': e.preventDefault(); editor.duplicate(); return;
                }
            }
            switch (e.code) {
                case 'KeyW': editor.state.setMode('move'); return;
                case 'KeyE': editor.state.setMode('rotate'); return;
                case 'KeyR': editor.state.setMode('scale'); return;
                case 'KeyQ': editor.state.setMode('select'); return;
                case 'Delete':
                    if (editor.selectedId) { e.preventDefault(); editor.deleteSelected(); }
                    return;
                case 'Escape':
                    if (editor.placing) { editor.cancel(); return; }
                    exitEditor();
                    return;
            }
        }
        if (switching) return;
        switch (e.code) {
            case 'F8':
                // Diagnostic: cycle gsplat debug mode (LOD colorize → octree
                // node AABBs → off) to see which LOD is actually rendering.
                cycleGsplatDebug();
                break;
            case 'KeyV':
                toggleMoveMode();
                break;
            case 'KeyR':
                void requestReset();
                break;
            case 'KeyE':
                if (pointerLock.locked) {
                    sceneManager.current?.hotspots.onClick(state);
                    sceneManager.current?.objects.onPressE(state);
                }
                break;
            case 'Escape':
                ui.hidePanel();
                break;
        }
    });

    // --- Main update loop (gated during switches) ---
    const playerPos = new Vec3();
    const lastSentPosition = new Vec3();
    let sendElapsed = 0;
    // 进房 ready 广播（player:joined）后主循环恢复运行的首帧立即发首个上行：
    // 服务端发给远端的初始 state 是未落地校准的出生点眼位，真实位置上行越早，
    // 远端「隐藏至位置同步」（RemotePlayers.confirmed）解除得越早，悬空窗口越短。
    let firstSend = true;
    // 第一人称行走朝向：帧间位移探测移动方向（与 ThirdPersonController._updateAvatar
    // 同款公式/阈值），移动中模型面向移动方向、停止或原地转身回正相机朝向，
    // 远端看到的不再是「侧身滑步」。prevViewMode 用于切回第一人称首帧重置基准。
    const prevFacePos = new Vec3();
    let prevViewMode: string | null = null;
    let resetPending = false;
    // 第一人称原地转向：根据 yaw 转速上报 walk 动画（迟滞防抖：>45°/s 触发，<20°/s 停止）
    let lastSentYaw: number | null = null;
    let turningWalk = false;
    async function requestReset() {
        if (resetPending || !session.online) return;
        resetPending = true;
        try { await session.reset(); } catch (e) { ui.showHint(String(e)); } finally { resetPending = false; }
    }
    walk.onNetworkReset = third.onNetworkReset = () => { void requestReset(); };
    const applyReset = (pose: PlayerState) => {
        state.position.set(...pose.position); active.onEnter(state); anim.reset(); rig.apply(state); lastSentPosition.copy(state.position); sendElapsed = 0;
        lastSentYaw = null; turningWalk = false; prevFacePos.copy(state.position);
    };
    session.onReset = applyReset;
    app.on('update', (dt: number) => {
        if (switching || (!session.online && !solo) || resetPending) {
            input.frame.read();
            remotes.update(session.serverTime);
            requestRenderFrame();
            return;
        }
        fpsAccum += dt;
        fpsFrames++;
        if (fpsMeter && fpsAccum >= 0.5) {
            fpsMeter.textContent = `${Math.round(fpsFrames / fpsAccum)} FPS${gsplatDebugShort[gsplatDebugIdx] ? ' · ' + gsplatDebugShort[gsplatDebugIdx] : ''}`;
            fpsAccum = 0;
            fpsFrames = 0;
        }
        input.update(dt);
        hostTools?.update();
        active.update(dt, input.frame, state);
        rig.apply(state);
        syncSelfAvatarVisible();
        sendElapsed += dt;
        if (viewMode === 'first') {
            if (prevViewMode !== 'first') {
                // 切回第一人称首帧（或进房/传送后）：重置位移基准，先对齐相机朝向。
                prevFacePos.copy(state.position);
                avatar.snapFacing(state.angles.y + 180);
            } else if (moveMode === 'walk') {
                // 行走朝向：水平位移速度超阈值（与第三人称一致的 0.3 m/s）时面向
                // 实际移动方向——atan2 约定与 ThirdPersonController._updateAvatar 相同；
                // 停止/原地转身则平滑回正相机朝向。位移而非输入取方向，贴墙滑步、
                // 被挤出等真实运动方向也正确。
                const fdx = state.position.x - prevFacePos.x, fdz = state.position.z - prevFacePos.z;
                if (Math.hypot(fdx, fdz) / Math.max(dt, 1e-4) > 0.3) avatar.setFacing(Math.atan2(fdx, fdz) * 180 / Math.PI, dt);
                else avatar.setFacing(state.angles.y + 180, dt);
                prevFacePos.copy(state.position);
            } else {
                // 飞行仍直接跟随相机朝向。
                avatar.snapFacing(state.angles.y + 180);
                prevFacePos.copy(state.position);
            }
            const speed = state.position.distance(lastSentPosition) / Math.max(0.001, sendElapsed);
            anim.update(dt, {speed, grounded: moveMode === 'fly' || walk.networkGrounded, jumping: moveMode === 'walk' && walk.networkJumping, running: walk.networkRunning || speed > 3});
        }
        prevViewMode = viewMode;
        if (sendElapsed >= 0.05 || firstSend) {
            const elapsed = Math.max(0.05, sendElapsed);
            const controller = viewMode === 'third' ? third : walk;
            const grounded = moveMode === 'walk' ? controller.networkGrounded : false;
            // 上行贴地钳制：上坡爬升期弹簧把眼位压在 ground+hover+eye 目标线之下
            // （本地相机不受影响），但远端按 眼位−feetDrop 重建的脚底会陷入坡面。
            // grounded 且探测到地面时，Y 按目标线上行；velocity / lastSentPosition
            // 同步用钳后值，保证远端插值/外推轨迹一致。
            const gY = controller.lastGroundY;
            let sendY = state.position.y;
            if (grounded && gY !== null) {
                const targetY = gY + controller.hoverHeight + controller.eyeHeight;
                if (sendY < targetY) sendY = targetY;
            }
            const velocity: [number, number, number] = [(state.position.x-lastSentPosition.x)/elapsed,(sendY-lastSentPosition.y)/elapsed,(state.position.z-lastSentPosition.z)/elapsed];
            const speed = Math.hypot(...velocity);
            // 第一人称 walk 模式下按 yaw 转速判定“原地转身走路”，让远端看到转身+走路动画
            let turning = false;
            if (viewMode === 'first' && moveMode === 'walk' && lastSentYaw !== null) {
                const yawDelta = (state.angles.y - lastSentYaw) % 360;
                const rate = Math.abs(yawDelta > 180 ? yawDelta - 360 : yawDelta < -180 ? yawDelta + 360 : yawDelta) / elapsed;
                turningWalk = turningWalk ? rate > 20 : rate > 45;
                turning = turningWalk;
            } else {
                turningWalk = false;
            }
            lastSentYaw = state.angles.y;
            const rotation = rig.avatarRoot.getRotation();
            session.publish({position:[state.position.x,sendY,state.position.z],rotation:[rotation.x,rotation.y,rotation.z,rotation.w],velocity,
                locomotion: classifyLocomotion(speed, moveMode, grounded, controller.networkRunning, turning), grounded, mic: voice.micEnabled});
            lastSentPosition.set(state.position.x, sendY, state.position.z); sendElapsed = 0; firstSend = false;
        }
        if (editor.enabled) editor.update(state);
        updateNetworkAction();
        hud.update();

        const inst = sceneManager.current;
        let sceneActivity = false;
        if (inst) {
            playerPos.copy(state.position);
            inst.hotspots.update(playerPos);
            inst.zones.update(playerPos);
            inst.portals.update(playerPos);
            sceneActivity = inst.objects.update(state, dt);
        }
        const othersPresent = remotes.update(session.serverTime);
        // 编辑器强退兜底（每帧检查）：中途有其他玩家进房，或主持人权限被
        // 收回/顶替（onModerationChanged 实时刷新 worldUI.isModerator）。
        if (editor.enabled && (othersPresent || !worldUI.isModerator)) {
            exitEditor();
            ui.showHint(othersPresent ? '有成员进入场景，已退出编辑模式' : '主持人权限已变更，已退出编辑模式', 5000);
        }
        updateMobileRenderGate(dt, othersPresent || sceneActivity);
    });

    // --- Boot: load the registry's entry scene ---
    ui.setStatus('正在加载场景……');
    let loadQueue: Promise<void> = Promise.resolve();
    let avatarKey = '';
    /** Rebuild the local avatar model (scene join + in-session avatar switch). */
    async function applyAvatar(av: Avatar): Promise<void> {
        const key = av.id + ':' + av.revision;
        if (key === avatarKey) return;
        avatarKey = key;
        avatar.destroy();
        avatar = new AvatarController(app, rig, modelConfig(av));
        avatar.setVisible(viewMode === 'third');
        await avatar.load();
        anim = new AnimationStateMachine(avatar);
        third.avatar = avatar;
        third.anim = anim;
        hud.setActions(modelConfig(av).actionAnims);
    }
    // 形象应用串行链：applyAvatar 非并发安全（destroy 后重建共享 avatar/anim/
    // third 引用），快速连续 onLoad（传送连跳）会让多次后台续体重叠，在链上
    // 排队互斥。优先级：等碰撞门（碰撞 GLB 独占完带宽）后立即下载/应用形象，
    // 落地或失败再释放形象门放行精细 LOD 预载。失败只记日志不外抛（默认形象
    // 已在场，无需回滚）。gates 是续体启动时捕获的本场景门——中途已切场景
    // 则释放的是旧门，无害（旧场景的预载启动均有 staleness 检查）。
    let avatarChain: Promise<void> = Promise.resolve();
    const queueAvatarYield = (av: Avatar) => {
        avatarChain = avatarChain.catch(() => {}).then(async () => {
            const gates = scenePreloadGates;
            try {
                if (gates) await gates.collision;
                await applyAvatar(av);
            } catch (err) {
                console.error('[scene] avatar apply failed:', err);
            } finally {
                console.info('[scene] avatar settled — releasing fine LOD preloads');
                gates?.releaseAvatar();
            }
        });
        return avatarChain;
    };
    session.onLoad = (admission: Admission) => {
        const work = loadQueue.catch(() => {}).then(async () => {
            remotes.clear();
            const manifest = {...admission.scene.manifest, hotspots: admission.scene.manifest.hotspots ?? [], triggerZones: admission.scene.manifest.triggerZones ?? [], portals: admission.scene.manifest.portals ?? [], defaultMode: admission.scene.manifest.defaultMode ?? 'fly', fallLimit: admission.scene.manifest.fallLimit ?? -100} as SceneManifest;
            manifestCache.set(admission.scene.id, manifest);
            // 先进入场景（显示加载进度），再后台加载形象：形象 GLB 体积大
            // （man ~19MB / woman ~24MB），手机端下载慢，若先 await 会长时间
            // 卡在「正在启动…」而看不到任何进度。
            await switchTo(admission.scene.id, admission.spawn, [admission.angles[0], admission.angles[1]]);
            lastSentPosition.copy(state.position); sendElapsed = 0; firstSend = true;
            // 传送/换场景后位置突变，行走朝向基准作废，首帧重新初始化。
            prevViewMode = null;
            // 场景已重建（旧 SceneObjectManager 连同动态物体一起销毁）：
            // 清空本地 diff 基线并按新房拉全量恢复主持人放置的物体。
            dynamicIds.clear();
            void pullRoomObjects(admission.room.id);
            // 进房/传送/断线恢复都会走 onLoad：VoiceClient.join 按频道幂等，
            // 同房间不重复加入，换房间自动切频道。
            joinVoice(admission.room.id);
            // 形象后台应用（不挡 onLoad 返回）：player:ready 等 onLoad 一经
            // 返回即发，解锁主循环的 online 门控（移动/视角）与快照——形象
            // 若挡在这条链上（曾串行等 full 预载 ≤15s + 下载形象 GLB 19-24MB），
            // 慢网络下进房后几十秒有画面却不能动。默认形象先顶上（初始化即
            // 有，拉取失败路径同理），串行续体等碰撞门后下载替换（碰撞 → 形象
            // → 精细 LOD 的带宽次序，见 scenePreloadGates），也把形象的主线程
            // 解析挪出进场敏感窗口。
            void queueAvatarYield(admission.avatar);
        });
        loadQueue = work; return work;
    };
    window.addEventListener('pagehide', () => { session.dispose(); remotes.destroy(); avatar.destroy(); voice.dispose(); hostTools?.destroy(); }, {once:true});
    try {
        if (solo) {
            // 世界场景（离线单人漫游）：不连接房间。场景 manifest 直接读静态
            // scene.json（spawn/碰撞/传送门等全部可用），形象取用户当前佩戴的。
            const work = loadQueue.catch(() => {}).then(async () => {
                // 先进入场景（显示加载进度），再后台拉取/加载形象，避免大 GLB
                // 下载拖慢「正在启动…」阶段（与 onLoad 保持一致）。
                await switchTo(business.invite.sceneId);
                lastSentPosition.copy(state.position); sendElapsed = 0;
                try {
                    const avatars = await business.api<{ id: string; name: string; modelUrl: string; config: Record<string, unknown> }[]>('/hub/avatars');
                    const mine = avatars.find((a) => a.id === business.user.avatarId) ?? avatars[0];
                    if (mine) {
                        // 形象走串行链（同 onLoad 优先级）：碰撞落地后、精细 LOD
                        // 之前下载应用；链上 finally 会释放形象门。
                        void queueAvatarYield({ ...mine, revision: 1, enabled: true } as Avatar);
                    } else {
                        scenePreloadGates?.releaseAvatar();
                    }
                } catch {
                    /* 形象拉取失败时沿用默认 avatarConfig 模型；释放形象门别拖精化。 */
                    scenePreloadGates?.releaseAvatar();
                }
            });
            loadQueue = work;
            await work;
        } else {
            bootStage('正在连接房间…');
            await session.start();
        }
    } catch (err) {
        // 满员是业务态而非初始化故障：toast 5 秒后自动回首页（entry 会重新进默认场景）。
        if (err instanceof Error && err.message === 'ROOM_FULL') {
            ui.showHint('房间人数已满', 5000);
            window.setTimeout(() => location.assign('/'), 5500);
            return;
        }
        throw err;
    }
    // HUD/FPS/设置按钮在 switchTo 场景就绪后统一显示，这里不再提前显示。
    ui.showHint('按住左键旋转视角 · WASD 移动 · V 切换行走/飞行');
}

main().catch((err) => {
    console.error(err);
    const el = document.getElementById('error-message');
    if (el) el.textContent = `初始化失败：${err instanceof Error ? err.message : String(err)}`;
    document.getElementById('error-screen')?.classList.remove('hidden');
});
